import { useQuery } from "@tanstack/vue-query";

import { QUERY_KEYS } from "@/helpers/constants/queryKeys";
import fundersApiService from "@/services/modules/funders";

import type { GetFundersPayload, IFunder } from "@/models/funders";

const FUNDERS_CHUNK_SIZE = 50;

export const useFunderQuery = (id: IFunder["id"]) => {
  return useQuery({
    queryKey: QUERY_KEYS.FUNDERS.single(id),
    queryFn: () => fundersApiService.getFunder(id),
  });
};

export const useFundersQuery = (
  params: GetFundersPayload | (() => GetFundersPayload)
) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.FUNDERS.all, params],
    enabled: () => {
      const resolvedParams = typeof params === "function" ? params() : params;
      return !!resolvedParams.ids?.length;
    },
    queryFn: async () => {
      const resolvedParams = typeof params === "function" ? params() : params;
      let allFundersResponse: IFunder[] = [];

      if (resolvedParams.ids?.length) {
        // Handle chunked requests for multiple IDs
        const chunk = (arr: string[], size: number): string[][] =>
          Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
            arr.slice(i * size, i * size + size)
          );

        const chunkedIds = chunk(resolvedParams.ids, FUNDERS_CHUNK_SIZE);
        const promises = chunkedIds.map((idChunk) =>
          fundersApiService.getFunders({ ...resolvedParams, ids: idChunk })
        );
        const responses = await Promise.all(promises);
        allFundersResponse = responses.flatMap((response) => response.data);
      } else {
        const response = await fundersApiService.getFunders(resolvedParams);
        allFundersResponse = response.data;
      }

      return allFundersResponse;
    },
  });
};
